import { Stack, useNavigation, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useState } from 'react';
import { Text, XStack, YStack } from 'tamagui';

import PSButton from '@/components/ui/PSButton';
import PSLinkButton from '@/components/ui/PSLinkButton';
import PSOtpInput from '@/components/ui/PSOtpInput';
import { useAuthContext } from '@/src/lib/authContext';
import { useResendCountdown } from '@/src/hooks/useResendCountdown';
import { startSession } from '@/src/lib/sessionStore';
import authApi from '@/src/api/authApi';
import { PSErrorClass } from '@/src/api/apiError';
import { toast } from '@tamagui/toast/v2';
import { PSHeaderBackButtonOnTransparentHeader } from '@/components/PSHeaderButtons';

const OTP_LENGTH = 6;

/**
 * Auth step — verify the email OTP. The LAST step, always reached from
 * `email.tsx` (never from `details.tsx` directly — see that screen's
 * docstring on why saving a name no longer implies an email is coming).
 *
 * Header is a ← BACK to `email` (gesture ON): going back with the address still
 * in the draft IS "change email" — no separate link. Note that re-submitting
 * there issues a NEW challenge, which is also how you redirect the code to a
 * different address; a resend from this screen can't, since the address is fixed
 * on the challenge.
 *
 * Confirming here is what writes the email onto the user and completes the
 * profile. The response carries the same token again, not a new one, so `startSession`
 * is called mainly to refresh the stored user — this is the point
 * `isProfileComplete` becomes true. Then we close the whole auth modal, returning
 * to whatever PRESENTED it (Home, or the /event page), which reads the session
 * and re-renders logged-in.
 *
 * ── Closing the modal (the non-obvious bit) ──
 * `router.dismiss()` acts on the NEAREST stack — this group's own nested Stack
 * (app/auth/_layout.tsx) — so it just pops one step (→ email), NOT out of the
 * modal. To leave the flow entirely we go up to the ROOT navigator
 * (`useNavigation('/')`) and `goBack()` there: that pops the `auth` modal ENTRY
 * off the root stack, revealing the screen underneath (the presenter). This is
 * caller-agnostic — no hardcoded `/event` or `/` target.
 */
export default function AuthVerifyEmail() {
  const router = useRouter();
  // The ROOT navigator — dismissing the whole auth modal happens here, not on
  // this nested stack (see the module docstring).
  const rootNavigation = useNavigation('/');
  const { email, markSent, otpSend } = useAuthContext();
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | undefined>();
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const { remaining, canResend } = useResendCountdown('email', email);

  const closeAuthModal = () => {
    if (rootNavigation.canGoBack()) {
      rootNavigation.goBack();
    } else {
      router.replace('/');
    }
  };

  const verify = async (code: string) => {
    // Reached without a challenge (a deep link straight here) — the email step
    // is what issues one, so send them back rather than failing silently.
    if (!otpSend.email?.challengeId) {
      toast.error('Please enter your details again.');
      router.replace('/auth/email');
      return;
    }

    setVerifying(true);
    try {
      const user = await authApi.verifyOtp(otpSend.email.challengeId, code);
      await startSession(user);
      closeAuthModal();
    } catch (err) {
      // Only `wrong_arguments` is a verdict on the digits — see the fuller note
      // in verify-mobile. This screen has two extra failures that are NOT: a 409
      // if someone else verified the address in the gap, and a 401 if the token
      // went bad. Both are about the account, not the code, so they toast.
      if (err instanceof PSErrorClass && err.code === 'wrong_arguments') {
        setOtpError(err.message);
        setOtp('');
      } else {
        toast.error(
          err instanceof PSErrorClass
            ? err.message
            : 'Something went wrong. Please try again.'
        );
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!otpSend.email?.challengeId) {
      toast.error('Please enter your details again.');
      router.replace('/auth/email');
      return;
    }

    setResending(true);
    try {
      // The new id REPLACES the old one — the server destroys the previous
      // challenge. Only stamp the countdown once the send actually succeeded.
      const newChallengeId = await authApi.resendOtp(otpSend.email.challengeId);
      markSent('email', email, newChallengeId);
      setOtp('');
      setOtpError(undefined);
    } catch (err) {
      toast.error(
        err instanceof PSErrorClass
          ? err.message
          : 'Something went wrong. Please try again.'
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <Head>
        <title>Verify email · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerLeft: () => <PSHeaderBackButtonOnTransparentHeader />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButtonOnTransparentHeader />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <YStack px="$lg" pt="$xl" gap="$lg">
        <YStack gap="$xs">
          <Text fontSize="$large" fontWeight="$semibold" color="$textDark">
            Last Step
          </Text>
          <XStack gap={'$space.xs'} flexWrap="wrap">
            <Text color="$textDarkMuted">we&apos;ve sent an otp on</Text>

            <Text fontWeight={'$semibold'}>{email}</Text>
          </XStack>
        </YStack>
        <PSOtpInput
          value={otp}
          onChangeText={setOtp}
          error={otpError}
          autoFocus
          onFocus={() => setOtpError(undefined)}
          onComplete={verify}
          disabled={verifying}
        />
        <XStack items="baseline" gap="$xs">
          <Text color="$textDarkMuted">Didn&apos;t get an otp?</Text>
          <PSLinkButton
            title={
              resending
                ? 'Sending…'
                : canResend
                  ? 'Resend'
                  : `Resend (in ${remaining} seconds)`
            }
            disabled={!canResend || resending}
            onPress={handleResend}
          />
        </XStack>
        <PSButton
          title="Verify"
          loading={verifying}
          loadingTitle="Verifying"
          onPress={() => verify(otp)}
          disabled={otp.length < OTP_LENGTH}
        />
      </YStack>
    </>
  );
}
